import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import { ViewportScroller } from '@angular/common';

import { TranslateModule } from '@ngx-translate/core';
import { environment } from '@pt/environment';
import { Article, CommentModel } from '@pt/models';
import {
  ButtonModule,
  SpinnerModule,
} from '@hglobal/common';
import { InfiniteScrollDirective } from '@pt/directives';
import { UiStore, GoogleAnalyticEventName, GoogleAnalyticsService } from '@pt/services';
import { TextFormaterHelper } from '@pt/helpers';

import { COMMENTS_API, CommentsApiMockService, CommentsApiService } from './api';
import { CommentComponent, CommentFormComponent } from './components';
import { CommentsStore } from './comments.store';
import { ThreadDialog } from './components/thread/thread.dialog';

@Component({
  selector: 'pt-comments',
  templateUrl: './comments.component.html',
  styleUrl: './comments.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateModule,
    CommentFormComponent,
    CommentComponent,
    SpinnerModule,
    InfiniteScrollDirective,
    ButtonModule,
  ],
  providers: [
    {
      provide: COMMENTS_API,
      useClass: environment.useMocks ? CommentsApiMockService : CommentsApiService,
    },
    CommentsStore,
    ThreadDialog,
  ],
})
export class CommentsComponent implements OnInit {
  readonly uiStore = inject(UiStore);

  readonly #commentsStore = inject(CommentsStore);
  readonly #viewportScroller = inject(ViewportScroller);
  readonly #googleAnalyticsService = inject(GoogleAnalyticsService);

  comments = this.#commentsStore.comments;
  itemsCount = this.#commentsStore.itemsCount;
  isLoading = this.#commentsStore.isLoading;
  article = input<Article>();
  postId = input<number>();
  googleAnalyticEvent = input<GoogleAnalyticEventName>();
  editingCommentId: number;
  showScrollTopBtn = false;
  commentsCountChanged = output<void>();

  sortedComments = computed(() => this.comments()
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()));

  itemsCountChangedEffect = effect(() => {
    const lastUpdated = this.#commentsStore.lastUpdated();

    if (lastUpdated) {
      this.commentsCountChanged.emit();
    }
  });

  ngOnInit(): void {
    this.#commentsStore.setPostId(this.postId());
    this.#viewportScroller.setOffset([0, this.uiStore.headerHeight()]);
  }

  isEditing(commentId: number): boolean {
    return this.editingCommentId === commentId;
  }

  onEdit(commentId: number): void {
    this.editingCommentId = commentId;
  }

  cancelEdit(): void {
    this.editingCommentId = null;
  }

  onReply(comment: CommentModel): void {
    this.#commentsStore.openThreadDialog(comment);
  }

  onDelete(comment: CommentModel) {
    this.#commentsStore.deleteComment(comment);
    this.cancelEdit();
  }

  onSave(content: string, comment?: CommentModel): void {
    const updatedComment = {
      ...comment,
      content,
      article: this.postId(),
    };

    this.#commentsStore.saveComment(updatedComment);
    if (comment) {
      this.cancelEdit();
    } else {
      this.#viewportScroller.scrollToAnchor('comments');
    }

    if (this.googleAnalyticEvent()) {
      this.#googleAnalyticsService.pushCustomEvent(this.googleAnalyticEvent(), 'comment_added', {
        text: content,
        ...(this.googleAnalyticEvent() === 'news' && {
          category: TextFormaterHelper.transformToSnakeCase(this.article().category?.name ?? ''),
          subcategory: TextFormaterHelper.transformToSnakeCase(
            this.article().subcategory?.name ?? '',
          ),
        }),
      });
    }
  }

  loadMore(): void {
    this.#commentsStore.updateFilters({
      page: this.#commentsStore.filters().page + 1,
    });
  }

  onScrollPossitionChanged(scrollPosition: number): void {
    this.showScrollTopBtn = scrollPosition > 300;
  }

  scrollToPost(): void {
    this.#viewportScroller.scrollToPosition([0, 0]);
  }
}
